import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiInstance } from './useApi';
import { getCurrencySymbol, getPlanPrice, formatPlanPrice, formatAmount } from '../utils/pricing';

const CACHE_KEY = 'teamgrid_detected_currency';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

function getCachedCurrency() {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const { currency, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) return currency;
    }
  } catch {}
  return null;
}

function setCachedCurrency(currency) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ currency, timestamp: Date.now() }));
  } catch {}
}

/**
 * @param {object} [options]
 * @param {string} [options.billingCurrency] - Currency already known from billing profile (e.g. 'INR')
 * @param {string} [options.paymentGateway] - Gateway already known ('razorpay' -> INR, 'stripe' -> USD)
 */
export function useCurrency({ billingCurrency, paymentGateway } = {}) {
  // Whether the currency is known for sure (explicitly passed, cached, or IP-resolved).
  // Until this is true, callers that pick a payment gateway should wait instead of
  // assuming the default 'USD' (which would wrongly route Indian users to Stripe).
  const knownAtInit =
    !!billingCurrency || paymentGateway === 'razorpay' || paymentGateway === 'stripe' || !!getCachedCurrency();

  const [currency, setCurrency] = useState(() => {
    if (billingCurrency) return billingCurrency.toUpperCase();
    if (paymentGateway === 'razorpay') return 'INR';
    if (paymentGateway === 'stripe') return 'USD';
    return getCachedCurrency() || 'USD';
  });
  const [loading, setLoading] = useState(false);
  const [resolved, setResolved] = useState(knownAtInit);

  useEffect(() => {
    if (billingCurrency || paymentGateway) {
      const next = billingCurrency
        ? billingCurrency.toUpperCase()
        : paymentGateway === 'razorpay' ? 'INR' : 'USD';
      setCurrency(next);
      setResolved(true);
      return;
    }

    const cached = getCachedCurrency();
    if (cached) {
      setCurrency(cached);
      setResolved(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    apiInstance
      .get('/api/v1/public/razorpay-billing/detect-currency')
      .then((res) => {
        if (!cancelled && res.data?.data?.currency) {
          const detected = res.data.data.currency;
          setCurrency(detected);
          setCachedCurrency(detected);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          // Mark resolved even on failure so callers don't hang forever; they fall
          // back to the safe 'USD' default only after a genuine detection attempt.
          setResolved(true);
        }
      });

    return () => { cancelled = true; };
  }, [billingCurrency, paymentGateway]);

  const symbol = useMemo(() => getCurrencySymbol(currency), [currency]);
  // interval is optional and defaults to monthly, so existing callers are unaffected.
  const getPrice = useCallback((plan, interval) => getPlanPrice(plan, currency, interval), [currency]);
  const fmtPrice = useCallback((plan, interval) => formatPlanPrice(plan, currency, interval), [currency]);
  const fmtAmount = useCallback((amount) => formatAmount(amount, currency), [currency]);

  return {
    currency,
    loading,
    resolved,
    symbol,
    getPrice,
    formatPrice: fmtPrice,
    formatAmount: fmtAmount,
  };
}

export default useCurrency;
